<template>
  <div class="phone-input-row" v-click-outside="closeMenu">
    <div class="phone-input-country-wrap">
      <button
        type="button"
        class="form-control default-bordered-input phone-input-trigger"
        :disabled="disabled"
        @click="toggleMenu"
      >
        <img
          class="phone-flag"
          :src="flagUrl(selectedCountry.iso)"
          :alt="selectedCountry.name"
          width="22"
          height="16"
          loading="lazy"
        />
        <span class="phone-dial">+{{ selectedCountry.dial || "…" }}</span>
        <span class="phone-caret">▾</span>
      </button>

      <div v-if="menuOpen" class="phone-country-menu">
        <button
          v-for="c in countries"
          :key="c.iso"
          type="button"
          class="phone-country-option"
          :class="{ active: c.iso === countryIso }"
          @click="selectCountry(c.iso)"
        >
          <img
            class="phone-flag"
            :src="flagUrl(c.iso)"
            :alt="c.name"
            width="22"
            height="16"
            loading="lazy"
          />
          <span class="phone-opt-name">{{ c.name }}</span>
          <span class="phone-opt-dial">+{{ c.dial || "…" }}</span>
        </button>
      </div>
    </div>

    <input
      type="tel"
      class="form-control default-bordered-input phone-input-number"
      :value="displayNational"
      :placeholder="selectedCountry.placeholder"
      :disabled="disabled"
      inputmode="tel"
      autocomplete="tel-national"
      @input="onNationalInput"
    />
  </div>
</template>

<script>
import {
  PHONE_COUNTRIES,
  buildStoredPhone,
  digitsOnly,
  flagUrl,
  formatNationalDisplay,
  parseStoredPhone,
} from "@/helpers/phoneNumber.js";

const clickOutside = {
  mounted(el, binding) {
    el.__phoneOutside = (event) => {
      if (!el.contains(event.target)) binding.value(event);
    };
    document.addEventListener("click", el.__phoneOutside, true);
  },
  unmounted(el) {
    document.removeEventListener("click", el.__phoneOutside, true);
    delete el.__phoneOutside;
  },
};

export default {
  name: "PhoneInput",
  directives: {
    clickOutside,
  },
  props: {
    modelValue: { type: String, default: "" },
    value: { type: String, default: undefined },
    disabled: { type: Boolean, default: false },
  },
  emits: ["update:modelValue", "input"],
  data() {
    return {
      countries: PHONE_COUNTRIES,
      countryIso: "BR",
      nationalDigits: "",
      syncing: false,
      menuOpen: false,
    };
  },
  computed: {
    boundValue() {
      return this.modelValue !== undefined && this.modelValue !== null
        ? this.modelValue
        : this.value || "";
    },
    selectedCountry() {
      return (
        this.countries.find((c) => c.iso === this.countryIso) ||
        this.countries[0]
      );
    },
    displayNational() {
      return formatNationalDisplay(this.countryIso, this.nationalDigits);
    },
  },
  watch: {
    boundValue: {
      immediate: true,
      handler(v) {
        const current = buildStoredPhone(
          this.selectedCountry.iso,
          this.selectedCountry.dial,
          this.nationalDigits
        );
        if (digitsOnly(v) !== digitsOnly(current)) {
          this.applyFromModel(v || "");
        }
      },
    },
  },
  methods: {
    flagUrl,
    toggleMenu() {
      if (this.disabled) return;
      this.menuOpen = !this.menuOpen;
    },
    closeMenu() {
      this.menuOpen = false;
    },
    selectCountry(iso) {
      this.countryIso = iso;
      this.menuOpen = false;
      if (this.syncing) return;
      if (this.countryIso !== "OTHER") {
        this.nationalDigits = this.nationalDigits.slice(
          0,
          this.selectedCountry.nationalMax
        );
      }
      this.emitStored();
    },
    applyFromModel(value) {
      this.syncing = true;
      const parsed = parseStoredPhone(value);
      this.countryIso = parsed.iso;
      this.nationalDigits = parsed.national;
      this.syncing = false;
    },
    emitStored() {
      const stored = buildStoredPhone(
        this.selectedCountry.iso,
        this.selectedCountry.dial,
        this.nationalDigits
      );
      this.$emit("update:modelValue", stored);
      this.$emit("input", stored);
    },
    onNationalInput(e) {
      let d = digitsOnly(e.target.value);
      if (this.countryIso === "OTHER") {
        d = d.slice(0, 15);
      } else {
        d = d.slice(0, this.selectedCountry.nationalMax);
      }
      this.nationalDigits = d;
      this.emitStored();
    },
  },
};
</script>

<style scoped>
.phone-input-row {
  display: flex;
  gap: 8px;
  width: 100%;
  position: relative;
}
.phone-input-country-wrap {
  position: relative;
  flex: 0 0 128px;
  max-width: 128px;
}
.phone-input-trigger {
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  padding: 0.45rem 0.5rem;
  text-align: left;
  cursor: pointer;
  background: #fff;
}
.phone-input-trigger:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}
.phone-dial {
  flex: 1;
  font-size: 0.85rem;
  font-weight: 600;
}
.phone-caret {
  font-size: 0.7rem;
  opacity: 0.6;
}
.phone-flag {
  display: block;
  object-fit: cover;
  border-radius: 2px;
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.08);
  flex-shrink: 0;
}
.phone-country-menu {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  z-index: 40;
  min-width: 240px;
  max-height: 280px;
  overflow-y: auto;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  padding: 4px;
}
.phone-country-option {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  border: 0;
  background: transparent;
  padding: 8px 10px;
  border-radius: 6px;
  cursor: pointer;
  text-align: left;
}
.phone-country-option:hover,
.phone-country-option.active {
  background: #f3e8ff;
}
.phone-opt-name {
  flex: 1;
  font-size: 0.85rem;
}
.phone-opt-dial {
  font-size: 0.8rem;
  color: #6b7280;
}
.phone-input-number {
  flex: 1 1 auto;
  min-width: 0;
}
</style>
